/**
 * Vehicle Detail Page Component
 * /admin/vehicles/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import apiClient from '../api/client';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadVehicle();
  }, [id]);

  const loadVehicle = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getVehicle(id);
      setVehicle(data);
    } catch (err) {
      setError(err.message || 'Failed to load vehicle');
    } finally {
      setLoading(false);
    }
  };

  const getOwnershipTypeLabel = (type) => {
    const labels = {
      company: 'Company',
      investor: 'Investor',
      driver: 'Driver',
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'default',
      maintenance: 'warning',
      retired: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !vehicle) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Vehicle not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/vehicles')}>
          Back to Vehicles
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/vehicles')}>
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Vehicle Details
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/vehicles/${id}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Plate Number
                </Typography>
                <Typography variant="body1">{vehicle.plate_number}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={vehicle.status || 'active'}
                  color={getStatusColor(vehicle.status)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Make
                </Typography>
                <Typography variant="body1">{vehicle.make || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Model
                </Typography>
                <Typography variant="body1">{vehicle.model || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Year
                </Typography>
                <Typography variant="body1">{vehicle.year || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Vehicle Type
                </Typography>
                <Typography variant="body1">{vehicle.vehicle_type || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Color
                </Typography>
                <Typography variant="body1">{vehicle.color || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  VIN
                </Typography>
                <Typography variant="body1">{vehicle.vin || '-'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {vehicle.ownership_type && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ownership Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ownership Type
                  </Typography>
                  <Typography variant="body1">
                    {getOwnershipTypeLabel(vehicle.ownership_type)}
                  </Typography>
                </Grid>
                {vehicle.owner_investor && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Owner (Investor)
                    </Typography>
                    <Typography variant="body1">
                      {vehicle.owner_investor_name || vehicle.owner_investor}
                    </Typography>
                  </Grid>
                )}
                {vehicle.owner_driver && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Owner (Driver)
                    </Typography>
                    <Typography variant="body1">
                      {vehicle.owner_driver_name || vehicle.owner_driver}
                    </Typography>
                  </Grid>
                )}
                {vehicle.purchase_date && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Purchase Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(vehicle.purchase_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                {vehicle.purchase_amount && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Purchase Amount
                    </Typography>
                    <Typography variant="body1">
                      {vehicle.purchase_amount.toLocaleString()} AED
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate(`/admin/vehicles/${id}/edit`)}
                >
                  Edit Vehicle
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/admin/purchases')}
                >
                  View Purchase History
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default VehicleDetail;

